import { useContext, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext, UserContext } from '../../../../context';
import { TranscriptionDialogContext } from '../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import {
  backendService,
  TrackingCategory,
  trackingService,
  TrackingTranscriptionParams,
} from '../../../../services';
import type { Step } from './TranscriptionDialog.config';
import { HELPER_TEXT, STEP_TITLE, STEPS } from './TranscriptionDialog.config';
import TranscriptionDialogWrapper from './TranscriptionDialogWrapper';
import ComposerTitle from './steps/ComposerTitle';
import type { ITheme } from '../../../../theme';

const useStyles = makeStyles()((theme: ITheme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  root: {
    position: 'relative',
    width: '90%',
    maxWidth: '700px',
    padding: '2em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1em',
    boxShadow: `0px 0px 5px 0 ${theme.palette.primary.main}`,
    [theme.breakpoints.down('sm')]: {
      margin: '1em auto',
      padding: '1em',
    },
    maxHeight: '95vh', // Added to ensure modal doesn't exceed viewport height
  },
}));

export interface TranscriptionDialogScanProps {
  handleClose: () => void;
  handleProcessing?: (processing: number) => void;
  handleResolve?: (jobId: string, useTickets: boolean) => void;
  location?: (typeof TrackingTranscriptionParams.location)['songbook' | 'wp' | 'nav'];
}
const TranscriptionDialogScan: React.FC<TranscriptionDialogScanProps> = ({
  handleClose,
  handleProcessing,
  handleResolve,
  location,
}) => {
  const { t } = useTranslation();
  const { jobType } = useContext(AppConstantsContext);
  const { apriori, file, reset, source } = useContext(TranscriptionDialogContext);
  const { user } = useContext(UserContext);
  const { classes } = useStyles();
  const [submitting, setSubmitting] = useState(false);

  const steps: Step[] = [STEPS.COMPOSER_TITLE];
  const [currentStep, setCurrentStep] = useState<Step>(steps[0]);

  const onSubmit = (useTickets: boolean) => {
    setSubmitting(true);
    const trackingValues = {
      type: useTickets
        ? TrackingTranscriptionParams.type.full
        : TrackingTranscriptionParams.type.free,
      location,
      input: TrackingTranscriptionParams.input.scan,
    };
    trackingService.valueObjectEvent<{ type: string; location: string; input: string }>(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.create,
      { ...trackingValues },
    );

    return backendService.transcribeFile(
      file,
      useTickets,
      {
        apriori,
      },
      source,
      jobType,
      user?.token,
      (perc) => {
        handleProcessing(perc);
      },
      (jobId) => {
        handleResolve(jobId.toString(), useTickets);
      },
    );
  };

  const onClickBack = () => {
    setCurrentStep(steps[steps.indexOf(currentStep) - 1]);
  };

  const handleCloseDialog = () => {
    reset();
    setSubmitting(false);
    trackingService.event(TrackingCategory.Transcription, 'action_wizard_close');
    handleClose();
  };

  return (
    <Modal
      open
      className={classes.modal}
      onClose={handleCloseDialog}
      slotProps={{ backdrop: { sx: { backgroundColor: '#000000d6' } } }}
    >
      <Paper className={classes.root}>
        <TranscriptionDialogWrapper
          submitting={submitting}
          handleClose={handleCloseDialog}
          handleBackClick={onClickBack}
          currentStep={steps.indexOf(currentStep)}
          numberOfSteps={steps.length}
          title={t(STEP_TITLE[currentStep])}
          helperText={t(HELPER_TEXT[currentStep])}
        >
          {/* {currentStep === STEPS.INSTRUMENT_ORDER && (
        <InstrumentOrder
          onSubmit={() => setCurrentStep(STEPS.COMPOSER_TITLE)}
          customSubmitButton={customSubmitButton}
        />
      )} */}
          {currentStep === STEPS.COMPOSER_TITLE && <ComposerTitle onSubmit={onSubmit} />}
        </TranscriptionDialogWrapper>
      </Paper>
    </Modal>
  );
};

export default TranscriptionDialogScan;
